import React from 'react';
import { Link } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import { Box } from '@mui/material';
import { deepOrange } from '@mui/material/colors';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import TtyOutlinedIcon from '@mui/icons-material/TtyOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import CurrencyRupeeRoundedIcon from '@mui/icons-material/CurrencyRupeeRounded';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Buyer } from '../../../../../Models/Buyer';
import { UserStateEnum } from '../../hooks/useNavbarController';
import { SessionManager } from '../../../../../services/SessionManager/SessionManager';
import { getDateFromTimeStamp } from '../../../../../utils/DateAndTimeUtils';
import logoImageGreyThumbnail from '../../../../../assets/images/branding/gg_thumbnail_grey.png';
import dashboardImg from '../../../../../assets/images/navbar/dashboard.svg';

interface MobileNavbarProps {
  userState: UserStateEnum;
  buyer?: Buyer;
  unreadMsgCount?: number;
  unreadNotificationCount: number;
  openNotificationPanel: boolean;
  openFavPanel: boolean;
  currentPath: string;
  onLogoClick: () => void;
  onToggle: () => void;
  onNavigate: (path: string) => void;
  onMyProfileClick: () => void;
  onFavouritesClick: () => void;
  onOrdersClick: () => void;
  onContactUsClick: () => void;
  onSignOutClick: () => void;
  onNotificationClick: () => void;
}

const MobileNavbar: React.FC<MobileNavbarProps> = ({
  userState,
  buyer,
  unreadMsgCount,
  unreadNotificationCount,
  openNotificationPanel,
  openFavPanel,
  currentPath,
  onLogoClick,
  onToggle,
  onNavigate,
  onMyProfileClick,
  onFavouritesClick,
  onOrdersClick,
  onContactUsClick,
  onSignOutClick,
  onNotificationClick
}) => {
  const handleNavigation = (callback: () => void) => {
    onToggle();
    callback();
  };

  return (
    <div className="mobileNavbar">
      {/* Fixed Top Bar */}
      <div className="mobileNavbarTop">
        <button className="mobileNavbarMenuBtn" onClick={onToggle} aria-label="Menu">
          <i id="menuIcon" className="fas fa-bars"></i>
        </button>
        <Link to="/" onClick={onLogoClick} className="mobileNavbarLogo">
          <img className='mobileNavbarLogoImg' src={logoImageGreyThumbnail} alt="logo" />
        </Link>
        <div className="mobileNavbarSpacer"></div>
      </div>

      {/* Backdrop Overlay */}
      <div className="mobileNavbarBackdrop" onClick={onToggle}></div>

      {/* Slide-out Menu */}
      <div className="mobileNavbarContent">
        {/* User Profile Section */}
        {userState !== UserStateEnum.LoggedOut && buyer && (
          <div className="mobileNavbarUserProfile">
            <div className="mobileNavbarUserProfileContent">
              <Avatar
                alt={buyer.fullName}
                src={buyer.profileImage}
                sx={{ width: 60, height: 60, flexShrink: 0 }}>
                <Box sx={{ height: "100%", width: "100%", bgcolor: deepOrange[500], fontSize: '1.5rem' }} className="center">
                  {buyer.fullName[0]}
                </Box>
              </Avatar>
              <div className="mobileNavbarUserInfo">
                <h2 className="mobileNavbarUserName">{buyer.fullName}</h2>
                <p className="mobileNavbarUserSince">
                  User Since {buyer.createdAt ? getDateFromTimeStamp(buyer.createdAt) : '---'}
                </p>
                {/* Seller Details */}
                {userState === UserStateEnum.Freelancer && SessionManager.shared.isSeller() && (
                  <div className="mobileNavbarSellerSection">
                    <div className="mobileNavbarSellerBadge">
                      <i className="fas fa-check-circle"></i>
                      <span>Freelancer</span>
                    </div>
                    <button 
                      className="mobileNavbarDashboardBtn" 
                      onClick={() => handleNavigation(() => onNavigate('/freelancer-dashboard'))}>
                      <img src={dashboardImg} alt="dashboard" />
                      <span>Dashboard</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Navigation Links */}
        <div className="mobileNavbarMainLinks">
          {userState !== UserStateEnum.LoggedOut ? (
            <>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(onMyProfileClick)}>
                <AccountCircleOutlinedIcon />
                <span>My Profile</span>
              </div>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(onFavouritesClick)}>
                <FavoriteBorderOutlinedIcon />
                <span>Wishlist</span>
              </div>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(onOrdersClick)}>
                <ShoppingCartOutlinedIcon />
                <span>My Orders</span>
              </div>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(onContactUsClick)}>
                <TtyOutlinedIcon />
                <span>Contact Us</span>
              </div>
              {!SessionManager.shared.isSeller() && (
                <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(() => onNavigate("/become-a-freelancer"))}>
                  <CurrencyRupeeRoundedIcon />
                  <span>Become a Freelancer</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(() => onNavigate("/categories"))}>
                <CategoryRoundedIcon />
                <span>Categories</span>
              </div>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(() => onNavigate("/become-a-freelancer"))}>
                <CurrencyRupeeRoundedIcon />
                <span>Become a Freelancer</span>
              </div>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(() => onNavigate("/about"))}>
                <InfoIcon />
                <span>About us</span>
              </div>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(() => onNavigate("/FAQs"))}>
                <HelpOutlineIcon />
                <span>FAQ</span>
              </div>
            </>
          )}
        </div>

        {/* Bottom Section */}
        <div className={`mobileNavbarBottomLinks ${userState === UserStateEnum.LoggedOut ? 'logged-out' : ''}`}>
          {userState !== UserStateEnum.LoggedOut ? (
            <>
              <div className="mobileNavbarLinkItem logout" onClick={() => handleNavigation(onSignOutClick)}>
                <LogoutOutlinedIcon />
                <span>Sign Out</span>
              </div>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(() => onNavigate("/about"))}>
                <InfoIcon />
                <span>About us</span>
              </div>
              <div className="mobileNavbarLinkItem" onClick={() => handleNavigation(() => onNavigate("/FAQs"))}>
                <HelpOutlineIcon />
                <span>FAQ</span>
              </div>
            </>
          ) : (
            <button className="mobileNavbarLoginHighlightBtn" onClick={() => handleNavigation(() => onNavigate("/login"))}>
              <LogoutOutlinedIcon />
              <span>Login</span>
            </button>
          )}
        </div>
      </div>

      {/* Fixed Bottom Navigation Bar - Only for logged-in users */}
      {userState !== UserStateEnum.LoggedOut && (
        <div className="mobileNavbarBottom">
          <button 
            className={`mobileNavbarBottomItem ${currentPath === '/' || currentPath === '' ? 'active' : ''}`} 
            onClick={() => onNavigate("/")}>
            <HomeRoundedIcon />
          </button>
          <button 
            className={`mobileNavbarBottomItem ${currentPath.startsWith('/categories') ? 'active' : ''}`} 
            onClick={() => onNavigate("/categories")}>
            <CategoryRoundedIcon />
          </button>
          <button 
            className={`mobileNavbarBottomItem ${currentPath.startsWith('/my-messages') || currentPath.startsWith('/chat') ? 'active' : ''}`} 
            onClick={() => onNavigate("/my-messages")}>
            <ChatRoundedIcon />
            {unreadMsgCount !== 0 && <span className='mobileNavbarBottomBadge'>{unreadMsgCount}</span>}
          </button>
          <button 
            className={`mobileNavbarBottomItem ${openNotificationPanel ? 'active' : ''}`} 
            onClick={onNotificationClick}>
            <NotificationsRoundedIcon />
            {unreadNotificationCount !== 0 && <span className='mobileNavbarBottomBadge'>{unreadNotificationCount}</span>}
          </button>
          <button 
            className={`mobileNavbarBottomItem ${openFavPanel ? 'active' : ''}`} 
            onClick={onFavouritesClick}>
            <FavoriteRoundedIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileNavbar;
